import { useCallback } from 'react';
import { convertSpeechToText } from '../../../api/speech';

/**
 * Encapsulates audio-to-text transcription and insertion into textarea.
 */
export function useAudioTranscription({ textareaRef, inputValue, setInputValue, setToastMessage }) {
    const handleAudioRecorded = useCallback(async audioBlob => {
        try {
            setToastMessage && setToastMessage({
                message: 'Transcribing audio...',
                type: 'info',
            });

            const transcribedText = await convertSpeechToText(audioBlob, progress => {
                if (progress === 100) {
                    setToastMessage && setToastMessage({
                        message: 'Transcription complete',
                        duration: 2000,
                    });
                }
            });

            const cleanedText = transcribedText
                .trim()
                .replace(/[\r\n]+$/, '')
                .replace(/\s+/g, ' ');

            if (textareaRef.current) {
                const textarea = textareaRef.current;
                const cursorStart = textarea.selectionStart;
                const cursorEnd = textarea.selectionEnd;

                const textBefore = inputValue.substring(0, cursorStart);
                const textAfter = inputValue.substring(cursorEnd);

                const needsSpaceBefore = textBefore.length > 0 && !textBefore.endsWith(' ');
                const needsSpaceAfter = textAfter.length > 0 && !textAfter.startsWith(' ');

                const spaceBefore = needsSpaceBefore ? ' ' : '';
                const spaceAfter = needsSpaceAfter ? ' ' : '';
                const newValue = textBefore + spaceBefore + cleanedText + spaceAfter + textAfter;

                const newCursorPosition = cursorStart + spaceBefore.length + cleanedText.length + spaceAfter.length;

                setInputValue(newValue);

                setTimeout(() => {
                    if (textarea) {
                        textarea.focus();
                        textarea.setSelectionRange(newCursorPosition, newCursorPosition);
                        textarea.style.height = 'auto';
                        textarea.style.height = `${textarea.scrollHeight}px`;
                    }
                }, 0);
            } else {
                setInputValue(prevValue => {
                    const trimmedPrev = prevValue.trim();
                    return trimmedPrev ? `${trimmedPrev} ${cleanedText}` : cleanedText;
                });
            }
        } catch (error) {
            setToastMessage && setToastMessage({
                message: 'Failed to transcribe audio',
                type: 'error',
            });
        }
    }, [textareaRef, inputValue, setInputValue, setToastMessage]);

    return { handleAudioRecorded };
}
